// trainingSlice.js
// Single source of truth for all training courses — shared between the agent
// portal (trainingApi.js) and the admin portal (trainingAdminApi.js).
//
// Initialized by merging:
//   • trainingCourses   (mockTrainingData.js)  — full module/section content
//   • mockAdminCourses  (mockManagerData.js)   — enrollment & management stats
//
// Phase 2: remove mock initialization and seed from real API response instead.

import { createSlice } from '@reduxjs/toolkit';
import { trainingCourses, trainingExams } from '../features/training/mockTrainingData';
import { mockAdminCourses, mockAgentProgress, mockActivityLog } from '../features/training/mockManagerData';

// Build a lookup map of admin metadata by course ID
const adminMap = Object.fromEntries(mockAdminCourses.map((c) => [c.id, c]));

const mergedCourses = trainingCourses.map((c) => {
  const admin = adminMap[c.id] || {};
  return {
    ...c,
    // Overlay admin management fields (defaults for any course missing them)
    status: admin.status ?? 'published',
    mandatory: admin.mandatory ?? false,
    assignedTo: admin.assignedTo ?? ['all'],
    enrolledCount: admin.enrolledCount ?? 0,
    completedCount: admin.completedCount ?? 0,
    passCount: admin.passCount ?? 0,
    failCount: admin.failCount ?? 0,
    avgScore: admin.avgScore ?? 0,
    createdBy: admin.createdBy ?? 'Training Academy',
    moduleCount: admin.moduleCount ?? (c.modules?.length ?? 0),
  };
});

// Include any admin-only courses that aren't in trainingCourses (future-proof)
const trainingIds = new Set(trainingCourses.map((c) => c.id));
const adminOnlyCourses = mockAdminCourses
  .filter((c) => !trainingIds.has(c.id))
  .map((c) => ({ ...c, modules: c.modules ?? [] }));

const trainingSlice = createSlice({
  name: 'training',
  initialState: {
    courses: [...mergedCourses, ...adminOnlyCourses],
    // Exam questions keyed by courseId — seeded from mock, updated by admin editor
    exams: { ...trainingExams },
    // Agent progress — starts from mock data, accumulates real activity at runtime
    agentProgress: mockAgentProgress.map((p) => ({ ...p })),
    // Recent activity feed — starts from mock log, prepended by real events
    recentActivity: mockActivityLog.map((a) => ({ ...a })),
  },
  reducers: {
    /**
     * Add a new course or update an existing one (matched by id).
     * When saving from AdminCourseEditor, spreads over the existing course so
     * full section content is preserved during an edit.
     */
    upsertCourse(state, action) {
      const incoming = action.payload;
      const idx = state.courses.findIndex((c) => c.id === incoming.id);
      if (idx !== -1) {
        // Merge: keep existing fields the editor doesn't touch (e.g. enrolledCount)
        state.courses[idx] = { ...state.courses[idx], ...incoming };
      } else {
        // New course — prepend to list with sensible admin defaults
        state.courses.unshift({
          enrolledCount: 0,
          completedCount: 0,
          passCount: 0,
          failCount: 0,
          avgScore: 0,
          assignedTo: [],
          modules: [],
          ...incoming,
          status: incoming.status ?? 'draft',
        });
      }
    },

    /**
     * Refresh seeded mock course content after code/demo-data changes while
     * preserving admin/progress metadata stored in the persisted slice.
     */
    refreshSeededCourses(state) {
      mergedCourses.forEach((seededCourse) => {
        const idx = state.courses.findIndex((c) => c.id === seededCourse.id);
        if (idx !== -1) {
          state.courses[idx] = {
            ...state.courses[idx],
            ...seededCourse,
            enrolledCount: state.courses[idx].enrolledCount ?? seededCourse.enrolledCount,
            completedCount: state.courses[idx].completedCount ?? seededCourse.completedCount,
            passCount: state.courses[idx].passCount ?? seededCourse.passCount,
            failCount: state.courses[idx].failCount ?? seededCourse.failCount,
            avgScore: state.courses[idx].avgScore ?? seededCourse.avgScore,
          };
        } else {
          state.courses.push(seededCourse);
        }
      });

      state.exams = { ...trainingExams, ...state.exams };
    },

    /** Remove a course by ID (admin delete). */
    removeCourse(state, action) {
      state.courses = state.courses.filter((c) => c.id !== action.payload);
      // Also remove its exam data
      delete state.exams[action.payload];
    },

    /**
     * Save or update exam questions for a course.
     * Payload: { courseId, passMark, feedbackMode, questions: [] }
     */
    upsertExam(state, action) {
      const { courseId, ...examData } = action.payload;
      state.exams[courseId] = { ...(state.exams[courseId] ?? {}), ...examData };
    },

    /**
     * Upsert a single agent+course progress record.
     * Payload: { agentId, courseId, status?, progressPct?, examScore?, examPassed?,
     *            lastActivity?, agentName?, agentEmail?, agentBranch?, agentRole? }
     * The agent* identity fields are stored on the record so the admin can display
     * the real logged-in user's name even when they're not in mockAgents.
     */
    updateAgentProgress(state, action) {
      const { agentId, courseId, agentName, agentEmail, agentBranch, agentRole, ...fields } = action.payload;
      const idx = state.agentProgress.findIndex(
        (p) => p.agentId === agentId && p.courseId === courseId,
      );
      const agentMeta = agentName != null
        ? { agentName, agentEmail, agentBranch, agentRole }
        : {};
      if (idx !== -1) {
        state.agentProgress[idx] = { ...state.agentProgress[idx], ...fields, ...agentMeta };
      } else {
        state.agentProgress.unshift({
          agentId,
          courseId,
          status: 'not_started',
          progressPct: 0,
          examScore: null,
          examPassed: null,
          lastActivity: null,
          ...fields,
          ...agentMeta,
        });
      }
    },

    /**
     * Prepend a new event to the admin activity feed.
     * Payload: { id, type, agentName, courseTitle, score, timestamp }
     */
    addActivityEvent(state, action) {
      state.recentActivity.unshift(action.payload);
      if (state.recentActivity.length > 100) {
        state.recentActivity = state.recentActivity.slice(0, 100);
      }
    },
  },
});

export const { upsertCourse, refreshSeededCourses, removeCourse, upsertExam, updateAgentProgress, addActivityEvent } = trainingSlice.actions;
export default trainingSlice.reducer;
